import { NumberInput, Select, Stack, Switch, TextInput } from '@cowbell/ui';
import type { FC } from 'react';
import type { JsonSchema } from '../../types/moo-tasks.types';
import { SlackChannelPicker } from './SlackChannelPicker';

export interface WidgetProps {
  readonly label?: string;
  readonly value?: unknown;
  readonly onChange: (v: unknown) => void;
  readonly schema?: JsonSchema;
  /** Full form value — widgets that depend on sibling fields (e.g. Slack's
   *  picker needs target_type) read it here. */
  readonly form?: Record<string, unknown>;
  /** Merge several fields at once (e.g. the Slack picker sets target + channel_id). */
  readonly setFields?: (partial: Record<string, unknown>) => void;
}

// Widgets every output-channel config can use. The task wizard's SchemaForm
// layers its trigger-only widgets (cron, filter-builder) on top of these.
export const OUTPUT_WIDGETS: Record<string, FC<WidgetProps>> = {
  'slack-channel': SlackChannelPicker,
};

interface OutputSchemaFormProps {
  readonly schema: JsonSchema;
  readonly value: Record<string, unknown>;
  readonly onChange: (v: Record<string, unknown>) => void;
  /** Widget registry keyed by schema hint (format / x-widget). */
  readonly widgets?: Record<string, FC<WidgetProps>>;
}

/** Schema-driven config form shared by the task wizard's steps and the
 *  chat's async-task confirmation card — one source of truth for how an
 *  output channel's fields render.
 *
 *  Text fields accept {{variableName}} smart variables. The trigger catalog
 *  lists them and chat-agent substitutes them from the triggering entity. */
export function OutputSchemaForm({
  schema,
  value,
  onChange,
  widgets = OUTPUT_WIDGETS,
}: OutputSchemaFormProps) {
  const props = schema.properties ?? {};
  const set = (k: string, v: unknown) => onChange({ ...value, [k]: v });

  return (
    <Stack gap="sm">
      {Object.entries(props).map(([key, field]) => {
        // Internal/auto-set fields (e.g. Slack's resolved channel_id) are marked
        // x-hidden — they stay in the config value but must not render as inputs.
        if (field['x-hidden']) return null;

        // A field can declare that it only applies when sibling fields hold
        // given values (x-show-when). The dwell audience's user picker uses
        // this so it appears only for the "Specific users" mode.
        const showWhen = field['x-show-when'];
        if (
          showWhen &&
          Object.entries(showWhen).some(([k, v]) => value[k] !== v)
        ) {
          return null;
        }

        const label = field.title ?? key;
        const hint = field['x-widget'] ?? field.format;
        const Widget = hint ? widgets[hint] : undefined;

        if (Widget) {
          return (
            <Widget
              key={key}
              label={label}
              value={value[key]}
              onChange={(v: unknown) => set(key, v)}
              schema={field}
              form={value}
              setFields={(partial) => onChange({ ...value, ...partial })}
            />
          );
        }

        // A field can make its help string depend on another field's value
        // (x-help-by + x-help-options). Slack's "Target" uses this to show
        // channel guidance vs. DM (email) guidance based on "Target Type".
        const helpBy = field['x-help-by'];
        const helpOptions = field['x-help-options'];
        const selected = helpBy
          ? (value[helpBy] as string | undefined)
          : undefined;
        const description =
          (helpOptions && selected ? helpOptions[selected] : undefined) ??
          field.description;

        if (field.enum) {
          return (
            <Select
              key={key}
              label={label}
              comboboxProps={{ withinPortal: true, zIndex: 1400 }}
              description={description}
              data={field.enum.map(String)}
              value={(value[key] as string) ?? ''}
              onChange={(v) => set(key, v)}
            />
          );
        }

        if (field.type === 'number' || field.type === 'integer') {
          return (
            <NumberInput
              key={key}
              label={label}
              description={description}
              value={value[key] as number}
              onChange={(v) => set(key, v)}
            />
          );
        }

        if (field.type === 'boolean') {
          return (
            <Switch
              key={key}
              label={label}
              description={description}
              checked={!!value[key]}
              onChange={(e) => set(key, e.currentTarget.checked)}
            />
          );
        }

        return (
          <TextInput
            key={key}
            label={label}
            description={description}
            placeholder={description}
            value={(value[key] as string) ?? ''}
            onChange={(e) => set(key, e.currentTarget.value)}
          />
        );
      })}
    </Stack>
  );
}
